import { cacheClubShields } from '../state/club-shield-cache';
import { clubPath } from '../state/club-session';
/**
 * Clubs API Service (Microservicio de Clubes)
 * Conecta con los endpoints del backend y en caso de que aún no existan o fallen,
 * simula la llamada cargando el mock (.json) desde /mocks/clubs/
 */
import { apiSimulator } from './api-simulator.service';
import {
  BackendStadiumResponse,
  BackendStaffMember,
  BackendBalanceResponse,
  BackendLedgerResponse,
  ClubShieldConfig,
} from '../models/types';

export const clubsApi = {
  async getSummaries(ids: string[]): Promise<{ id: string; code: string; name: string }[]> {
    if (!ids.length) return [];
    const rows=await apiSimulator.callWithMockFallback<{clubId:string;clubName:string;clubCode?:string}[]>({
      method:'GET',endpoint:clubPath('/standings'),service:'competition',mockName:'standings',allowMockFallback:false,
    });
    return rows.filter(row=>ids.includes(row.clubId)).map(row=>({id:row.clubId,name:row.clubName,code:row.clubCode || ''}));
  },

  /**
   * GET /api/clubs/{clubId}
   * Recupera los datos básicos de un club
   */
  async getClub(clubId: string): Promise<any> {
    return apiSimulator.callWithMockFallback<any>({
      method: 'GET',
      endpoint: `/api/clubs/${encodeURIComponent(clubId)}`,
      service: 'clubs',
      mockName: 'clubs-list',
      transformMock: (mock) => {
        const list = mock?.content || (Array.isArray(mock) ? mock : []);
        const found = list.find((c: any) => c.id === clubId);
        if (found) return found;
        return {
          id: clubId,
          name: `Club ${clubId}`,
          code: clubId.slice(0, 3).toUpperCase(),
        };
      },
    });
  },

  /**
   * GET /api/clubs
   * Real endpoint: https://api.lom-dev.dpyra.es/api/clubs
   * Mock fallback: /mocks/clubs/clubs-list.json
   */
  async getRealClubs(
    page: number = 0,
    size: number = 20
  ): Promise<{ content: any[]; totalElements: number; totalPages: number }> {
    return apiSimulator.callWithMockFallback({
      method: 'GET',
      endpoint: `/api/clubs?page=${page}&size=${size}`,
      service: 'clubs',
      mockName: 'clubs-list',
    });
  },

  /**
   * POST /api/clubs
   * Alta de club en la competición global
   * Mock fallback: /mocks/clubs/create-club.json
   */
  async createClub(name: string): Promise<any> {
    return apiSimulator.callWithMockFallback({
      method: 'POST',
      endpoint: '/api/clubs',
      body: { name },
      service: 'clubs',
      mockName: 'create-club',
      transformMock: (mock) => ({
        ...mock,
        name,
        id: `club-user-${Date.now()}`,
      }),
    });
  },

  /**
   * GET /api/clubs/{clubId}/squad
   * Recupera la plantilla de jugadores del club desde el backend real
   * Mock fallback: /mocks/clubs/club-squad.json
   */
  async getClubSquad(clubId: string): Promise<any> {
    return apiSimulator.callWithMockFallback({
      method: 'GET',
      endpoint: `/api/clubs/${encodeURIComponent(clubId)}/squad`,
      service: 'clubs',
      mockName: 'club-squad',
      transformMock: (mock) => {
        if (Array.isArray(mock)) {
          return mock.map((p) => ({ ...p, clubId }));
        }
        return mock;
      },
    });
  },

  /**
   * GET /api/clubs/{clubId}/stadium
   * Recupera el nivel de los módulos comerciales y explotación del estadio
   * Mock fallback: /mocks/clubs/club-stadium.json
   */
  async getClubStadium(clubId: string): Promise<BackendStadiumResponse> {
    return apiSimulator.callWithMockFallback<BackendStadiumResponse>({
      method: 'GET',
      endpoint: `/api/clubs/${encodeURIComponent(clubId)}/stadium`,
      service: 'clubs',
      mockName: 'club-stadium',
      transformMock: (mock) => ({
        ...mock,
        clubId,
      }),
    });
  },

  /**
   * GET /api/clubs/{clubId}/staff
   * Recupera el cuerpo técnico y preparadores del club
   * Mock fallback: /mocks/clubs/club-staff.json
   */
  async getClubStaff(clubId: string): Promise<BackendStaffMember[]> {
    return apiSimulator.callWithMockFallback<BackendStaffMember[]>({
      method: 'GET',
      endpoint: `/api/clubs/${encodeURIComponent(clubId)}/staff`,
      service: 'clubs',
      allowMockFallback: false,
      mockName: 'club-staff',
      transformMock: (mock) => {
        if (Array.isArray(mock)) {
          return mock.map((s) => ({ ...s, clubId }));
        }
        return mock;
      },
    });
  },

  /**
   * GET /api/clubs/{clubId}/economy/balance
   * Recupera el balance de dinero del club (total, reservado, disponible)
   * Mock fallback: /mocks/clubs/club-economy-balance.json
   */
  async getClubEconomyBalance(clubId: string): Promise<BackendBalanceResponse> {
    return apiSimulator.callWithMockFallback<BackendBalanceResponse>({
      method: 'GET',
      endpoint: `/api/clubs/${encodeURIComponent(clubId)}/economy/balance`,
      service: 'clubs',
      allowMockFallback: false,
      mockName: 'club-economy-balance',
      transformMock: (mock) => ({
        ...mock,
        clubId,
      }),
    });
  },

  /**
   * GET /api/clubs/{clubId}/economy/ledger
   * Recupera el libro mayor de movimientos contables del club
   * Mock fallback: /mocks/clubs/club-economy-ledger.json
   */
  async getClubEconomyLedger(
    clubId: string,
    page: number = 0,
    size: number = 20
  ): Promise<BackendLedgerResponse> {
    return apiSimulator.callWithMockFallback<BackendLedgerResponse>({
      method: 'GET',
      endpoint: `/api/clubs/${encodeURIComponent(clubId)}/economy/ledger?page=${page}&size=${size}`,
      service: 'clubs',
      mockName: 'club-economy-ledger',
    });
  },

  /**
   * GET /api/clubs/{clubId}/shield
   * Obtiene la configuración guardada del escudo personalizado del club
   */
  async getClubShield(clubId: string): Promise<ClubShieldConfig | null> {
    return apiSimulator.callWithMockFallback<ClubShieldConfig | null>({
      method: 'GET',
      endpoint: `/api/clubs/${encodeURIComponent(clubId)}/shield`,
      service: 'clubs',
      mockName: 'club-shield',
      allowMockFallback: false,
    });
  },

  /**
   * PUT /api/clubs/{clubId}/shield
   * Guarda la configuración del escudo personalizado del club
   */
  async updateClubShield(
    clubId: string,
    shield: ClubShieldConfig
  ): Promise<ClubShieldConfig> {
    const res = await apiSimulator.callWithMockFallback<ClubShieldConfig>({
      method: 'PUT',
      endpoint: `/api/clubs/${encodeURIComponent(clubId)}/shield`,
      body: shield,
      service: 'clubs',
      mockName: 'club-shield',
      allowMockFallback: false,
    });
    if (!res || !res.textColor || (shield.textColor && res.textColor.toUpperCase() !== shield.textColor.toUpperCase())) {
      throw new Error('El servidor no ha confirmado el color del texto. Comprueba que el backend esté actualizado e inténtalo de nuevo.');
    }
    try {
      if (typeof localStorage !== 'undefined') {
        localStorage.setItem(`lom_club_shield_${clubId}`, JSON.stringify(res));
      }
    } catch {}
    cacheClubShields([{ clubId, shield: res }]);
    return res;
  },
};
